import React from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { TonConnectUIProvider } from "@tonconnect/ui-react";
import { AppRoot } from "@telegram-apps/telegram-ui";
import { TON_CONNECT_MANIFEST_URL } from "./configs/config";
import I18nProvider from "./utils/i18n/I18nProvider";
import { AppWithLoadingState } from "./components/App/AppWithLoadingState";
import { ErrorProvider } from "./utils/error";
import { ModalManagerProvider } from "./utils/modal/ModalManager";
import { GameStoreProvider } from "./store/GameStoreProvider";
import DesktopLanding from "./components/DesktopLanding/DesktopLanding";
import { MainMenuPage } from "./pages/MainMenuPage";
import { ShopPage } from "./pages/ShopPage";
import { InvitePage } from "./pages/InvitePage";
import { LeaderboardPage } from "./pages/LeaderboardPage";
import { TasksPage } from "./pages/TasksPage";
import { SettingsPage } from "./pages/SettingsPage";
import { CharacterLooksPage } from "./pages/CharacterLooksPage";
import { CharacterUpgradePage } from "./pages/CharacterUpgradePage";
import { StageSelectPage } from "./pages/StageSelectPage";
import { MagicBooksPage } from "./pages/MagicBooksPage";
import { ManaPage } from "./pages/ManaPage";
import { GameComponent } from "./components/Game/Game";
import isDesktop from "./utils/isDesktop";
import { setNavigateCallback } from "./scripts/game/Router";

interface AppProps {
  platform?: string;
}

function AppRoutes() {
  const navigate = useNavigate();

  // Set up navigation callback for Phaser game code
  React.useEffect(() => {
    setNavigateCallback((path: string) => navigate(path));
  }, [navigate]);

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/menu" replace />} />
      <Route path="/menu" element={<MainMenuPage />} />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/invite" element={<InvitePage />} />
      <Route path="/leaderboard" element={<LeaderboardPage />} />
      <Route path="/tasks" element={<TasksPage />} />
      <Route path="/mana" element={<ManaPage />} />
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="/character-looks" element={<CharacterLooksPage />} />
      <Route
        path="/character-looks/:characterId"
        element={<CharacterLooksPage />}
      />
      <Route path="/character-upgrade" element={<CharacterUpgradePage />} />
      <Route
        path="/character-upgrade/:characterId"
        element={<CharacterUpgradePage />}
      />
      <Route path="/stage-select" element={<StageSelectPage />} />
      <Route path="/magic-books" element={<MagicBooksPage />} />
      <Route path="/game" element={<GameComponent />} />
      <Route path="*" element={<Navigate to="/menu" replace />} />
    </Routes>
  );
}

export function App({ platform }: AppProps) {
  if (isDesktop()) {
    return <DesktopLanding />;
  }

  return (
    <AppRoot className="app-root">
      <TonConnectUIProvider manifestUrl={TON_CONNECT_MANIFEST_URL}>
        <I18nProvider>
          <GameStoreProvider>
            <AppWithLoadingState>
              <ErrorProvider>
                <ModalManagerProvider>
                  <AppRoutes />
                </ModalManagerProvider>
              </ErrorProvider>
            </AppWithLoadingState>
          </GameStoreProvider>
        </I18nProvider>
      </TonConnectUIProvider>
    </AppRoot>
  );
}
